import {
  ApiBlueprint,
  AppRootElementBlueprint,
  createFrontendModule,
  errorApiRef,
  identityApiRef,
  storageApiRef,
} from '@backstage/frontend-plugin-api';
import { HomePageLayoutBlueprint } from '@backstage/plugin-home-react/alpha';
import {
  VisitsStorageApi,
  VisitsWebStorageApi,
  visitsApiRef,
  type VisitInput,
} from '@backstage/plugin-home';
import { VisitRecorder } from './VisitRecorder';

const TRACKED_ENTITY_KINDS = new Set([
  'domain',
  'system',
  'repository',
  'api',
  'component',
  'user',
  'group',
]);

const canSaveVisit = ({ pathname }: VisitInput) => {
  const [root, , kind, name] = pathname.split('/').filter(Boolean);
  return root === 'catalog' && !!name && TRACKED_ENTITY_KINDS.has(kind);
};

const visitsApi = ApiBlueprint.make({
  name: 'visits',
  params: defineParams =>
    defineParams({
      api: visitsApiRef,
      deps: {
        storageApi: storageApiRef,
        identityApi: identityApiRef,
        errorApi: errorApiRef,
      },
      factory: ({ storageApi, identityApi, errorApi }) => {
        if (storageApi) {
          return VisitsStorageApi.create({
            storageApi,
            identityApi,
            canSave: canSaveVisit,
          });
        }
        return VisitsWebStorageApi.create({ identityApi, errorApi });
      },
    }),
});

/*
 * The home plugin ships both the visits API and its listener `disabled: true`,
 * and listing them under `app.extensions` is not enough to switch them back on
 * — only a same-id override in this module is. That is why `api:home/visits`
 * above has always worked and the stock listener never did. Both the override
 * and the replacement component are needed for Recently viewed to fill up; see
 * `VisitRecorder` for why the stock listener does not work here.
 */
const visitListener = AppRootElementBlueprint.make({
  name: 'visit-listener',
  params: { element: <VisitRecorder /> },
});

const homeLayout = HomePageLayoutBlueprint.make({
  name: 'hone',
  params: {
    loader: async () => (await import('./HomePage')).HomePage,
  },
});

export const homeModule = createFrontendModule({
  pluginId: 'home',
  extensions: [homeLayout, visitsApi, visitListener],
});
